// Passport Index API functions
// Using public REST API endpoint
const PASSPORT_INDEX_API = 'https://passportindex.org/api/v2/';

// Backend API URL - uses environment variable in production, localhost in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Hardcoded visa requirements for non-tourist purposes when API shows visa-free
// These are common cases where visa-free entry doesn't apply to work/study/residence
const WORK_STUDY_OVERRIDES = {
  'United States-United Kingdom-work': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'United States-United Kingdom-student': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'United States-United Kingdom-resident': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'Canada-United Kingdom-work': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'Canada-United Kingdom-student': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'Canada-United Kingdom-resident': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'Australia-United Kingdom-work': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'Australia-United Kingdom-student': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'Australia-United Kingdom-resident': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'New Zealand-United Kingdom-work': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'New Zealand-United Kingdom-student': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
  'New Zealand-United Kingdom-resident': { visaRequired: true, visaType: 3, visaTypeLabel: 'Visa Required' },
};

// OpenAI API configuration
// Note: API key is now stored on the server in .env file
// No need to set it from the frontend

export const setOpenAIKey = (key) => {
  // Deprecated: API key is now managed server-side
  console.log('Note: OpenAI API key is now configured on the server via .env file');
};

// Convert visa type number to label
const getVisaTypeLabel = (type) => {
  const labels = {
    0: 'Visa-Free',
    1: 'E-Visa',
    2: 'Visa on Arrival',
    3: 'Visa Required'
  };
  return labels[type] || 'Unknown';
};

// Function to get visa info from OpenAI when API data is missing
export const getVisaInfoFromOpenAI = async (passportCountry, destinationCountry, visaType = 'tourist') => {
  console.log('getVisaInfoFromOpenAI called for', passportCountry, 'to', destinationCountry, 'for', visaType);

  try {
    console.log(`Calling OpenAI proxy for ${passportCountry} to ${destinationCountry} (${visaType})`);
    
    // First, check if backend server is running
    try {
      const healthCheck = await fetch(`${BACKEND_URL}/api/health`, {
        method: 'GET',
        timeout: 5000
      });
      
      if (!healthCheck.ok) {
        console.warn('Backend server is not responding properly');
      }
    } catch (healthError) {
      console.warn('Backend server is not running. Make sure to run: npm run server in another terminal');
      return null;
    }
    
    // Call the backend proxy (API key is stored server-side, no need to pass it)
    const response = await fetch(`${BACKEND_URL}/api/visa-openai`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        passportCountry,
        destinationCountry,
        visaType
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('OpenAI proxy error:', response.status, errorData);
      throw new Error(`OpenAI error: ${response.status} - ${errorData.error || 'Unknown error'}`);
    }

    const visaInfo = await response.json();
    console.log(`OpenAI returned: ${passportCountry} to ${destinationCountry}:`, visaInfo);
    
    return {
      visaRequired: visaInfo.visaRequired,
      visaType: visaInfo.visaType,
      visaTypeLabel: visaInfo.visaTypeLabel || getVisaTypeLabel(visaInfo.visaType),
      details: visaInfo.details || '',
      source: 'openai'
    };
  } catch (error) {
    console.error('Error calling OpenAI via proxy:', error);
    return null;
  }
};

// List of countries for fallback if API fails
const FALLBACK_COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda",
  "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas",
  "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize",
  "Bermuda",
  "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil",
  "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon",
  "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", "China",
  "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus",
  "Czech Republic", "Czechia", "Democratic Republic of the Congo", "Denmark",
  "Djibouti", "Dominica", "Dominican Republic", "East Timor", "Ecuador", "Egypt",
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia", "Fiji",
  "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
  "Gibraltar", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau",
  "Guyana", "Haiti", "Honduras", "Hong Kong", "Hungary", "Iceland", "India",
  "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan",
  "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan",
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein",
  "Lithuania", "Luxembourg", "Macao", "Madagascar", "Malawi", "Malaysia",
  "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius",
  "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro",
  "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia",
  "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea",
  "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania",
  "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe",
  "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore",
  "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa",
  "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname",
  "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
  "Thailand", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
  "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates",
  "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu",
  "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

// Cache for country list and visa data
let countriesCache = null;
let visaDataCache = {};

// Get all countries from API
export const getAllCountries = async () => {
  if (countriesCache) return countriesCache;
  
  try {
    // Try to fetch from the API
    const response = await fetch(`${PASSPORT_INDEX_API}countries`);
    
    if (!response.ok) {
      throw new Error(`API returned status ${response.status}`);
    }
    
    const data = await response.json();
    
    // Handle different possible response formats
    let countries = [];
    
    if (Array.isArray(data)) {
      countries = data.map(item => typeof item === 'string' ? item : item.name || item.country).filter(Boolean);
    } else if (typeof data === 'object') {
      countries = Object.values(data)
        .map(item => {
          if (typeof item === 'string') return item;
          if (item.name) return item.name;
          if (item.country) return item.country;
          return null;
        })
        .filter(Boolean);
    }
    
    if (countries.length === 0) {
      throw new Error('No countries found in API response');
    }
    
    countriesCache = countries.sort();
    return countriesCache;
  } catch (error) {
    console.warn('Failed to fetch countries from API, using fallback list:', error);
    // Return fallback list if API fails
    countriesCache = FALLBACK_COUNTRIES.sort();
    return countriesCache;
  }
};

// Get visa requirements for a specific passport and destination
export const getVisaRequirements = async (passportCountry, destinationCountry, visaType = 'tourist') => {
  const cacheKey = `${passportCountry}-${destinationCountry}-${visaType}`;
  
  // Check cache
  if (visaDataCache[cacheKey]) {
    console.log(`Cache hit for ${cacheKey}`);
    return visaDataCache[cacheKey];
  }
  
  try {
    // Try with the exact country name first
    let response = await fetch(`${PASSPORT_INDEX_API}passport/${encodeURIComponent(passportCountry)}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch visa data for ${passportCountry}`);
    }
    
    const data = await response.json();
    
    // Handle different possible response formats
    let destinations = data.destinations || data.country?.destinations || data.data?.destinations || {};
    
    console.log(`API response for ${passportCountry}:`, { destinationCount: Object.keys(destinations).length, sample: Object.keys(destinations).slice(0, 3) });
    
    let result = {
      visaRequired: null,
      visaType: null,
      visaTypeLabel: 'Data unavailable',
      backendNotRunning: false
    };
    
    // Try exact match first
    let destInfo = destinations[destinationCountry];
    
    // If not found, try case-insensitive search
    if (!destInfo) {
      const destKey = Object.keys(destinations).find(key => 
        key.toLowerCase() === destinationCountry.toLowerCase()
      );
      destInfo = destKey ? destinations[destKey] : null;
    }
    
    if (destInfo) {
      const apiVisaType = destInfo.visa !== undefined ? destInfo.visa : destInfo.visaType;
      // Visa-free: 0, E-Visa: 1, Visa on Arrival: 2, Visa Required: 3
      if (apiVisaType !== null && apiVisaType !== undefined) {
        console.log(`Visa info for ${passportCountry} to ${destinationCountry}: apiVisaType=${apiVisaType}, requestedVisaType=${visaType}, label=${getVisaTypeLabel(apiVisaType)}`);
        
        // For non-tourist visa types, the API data may be incomplete
        // (e.g., visa-free for tourism doesn't mean visa-free for work)
        // Check if there's a hardcoded override for this case
        const overrideKey = `${passportCountry}-${destinationCountry}-${visaType}`;
        if (apiVisaType === 0 && WORK_STUDY_OVERRIDES[overrideKey]) {
          console.log(`Using hardcoded override for ${overrideKey}`);
          const override = WORK_STUDY_OVERRIDES[overrideKey];
          const cacheResult = {
            visaRequired: override.visaRequired,
            visaType: override.visaType,
            visaTypeLabel: override.visaTypeLabel,
            source: 'hardcoded-override',
            backendNotRunning: false
          };
          visaDataCache[cacheKey] = cacheResult;
          return cacheResult;
        }
        
        // If no hardcoded override, try OpenAI for non-tourist visa types
        const needsOpenAICheck = apiVisaType === 0 && ['work', 'student', 'business', 'resident'].includes(visaType.toLowerCase());
        console.log(`needsOpenAICheck: ${needsOpenAICheck}, apiVisaType=${apiVisaType}, visaType=${visaType}`);
        
        if (needsOpenAICheck) {
          console.log(`API shows visa-free but visa type is ${visaType}, consulting OpenAI for accurate ${visaType} requirements...`);
          const openaiResult = await getVisaInfoFromOpenAI(passportCountry, destinationCountry, visaType);
          if (openaiResult) {
            console.log(`OpenAI returned: visaRequired=${openaiResult.visaRequired}, visaType=${openaiResult.visaType}`);
            const cacheResult = {
              visaRequired: openaiResult.visaRequired,
              visaType: openaiResult.visaType,
              visaTypeLabel: openaiResult.visaTypeLabel,
              source: 'openai-override',
              backendNotRunning: false
            };
            visaDataCache[cacheKey] = cacheResult;
            return cacheResult;
          }
        }
        
        result.visaRequired = apiVisaType === 3;
        result.visaType = apiVisaType;
        result.visaTypeLabel = getVisaTypeLabel(apiVisaType);
        visaDataCache[cacheKey] = result;
        return result;
      }
    }
    
    // If API has no data, try OpenAI
    // OpenAI is now always available if backend server is running and has API key configured
    console.log(`No data from Passport Index for ${passportCountry} to ${destinationCountry}, trying OpenAI for ${visaType}...`);
    const openaiResult = await getVisaInfoFromOpenAI(passportCountry, destinationCountry, visaType);
    
    if (openaiResult) {
      const cacheResult = {
        visaRequired: openaiResult.visaRequired,
        visaType: openaiResult.visaType,
        visaTypeLabel: openaiResult.visaTypeLabel,
        source: 'openai',
        backendNotRunning: false
      };
      visaDataCache[cacheKey] = cacheResult;
      return cacheResult;
    } else {
      // OpenAI returned null - check if backend is running
      try {
        await fetch('http://localhost:5000/api/health', { timeout: 5000 });
        // Backend is running but OpenAI call failed for some reason
        result.backendNotRunning = false;
      } catch {
        // Backend is not running
        result.backendNotRunning = true;
      }
    }
    
    // If both APIs have no data, return unavailable
    visaDataCache[cacheKey] = result;
    return result;
  } catch (error) {
    console.warn(`Could not fetch visa data for ${passportCountry} to ${destinationCountry}:`, error.message);
    
    // Try OpenAI as final fallback
    try {
      console.log(`Passport Index failed, trying OpenAI for ${passportCountry} to ${destinationCountry} (${visaType})...`);
      const openaiResult = await getVisaInfoFromOpenAI(passportCountry, destinationCountry, visaType);
      
      if (openaiResult) {
        const cacheResult = {
          visaRequired: openaiResult.visaRequired,
          visaType: openaiResult.visaType,
          visaTypeLabel: openaiResult.visaTypeLabel,
            source: 'openai'
          };
          visaDataCache[cacheKey] = cacheResult;
          return cacheResult;
        }
      } catch (openaiError) {
        console.warn('OpenAI fallback also failed:', openaiError);
      }
    
    return {
      visaRequired: null,
      visaType: null,
      visaTypeLabel: 'Data unavailable'
    };
  }
};

// Get visa info for multiple passports to a destination
export const getMultipleVisaRequirements = async (passports, destination, visaType = 'tourist') => {
  const results = await Promise.all(
    passports.map(passport => getVisaRequirements(passport, destination, visaType))
  );
  
  return passports.map((passport, idx) => ({
    passport,
    ...results[idx]
  }));
};
